import type { AuthUser } from '@fluxus/shared';
import { Menu } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { buildNavSections, mobilePrimaryItems, type NavItem, type NavSection } from '@/app/navigation';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { ThemeMenu } from '@/components/theme/theme';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { UserMenu } from './UserMenu';

type AppShellProps = {
  items: NavItem[];
  /** Topo do menu (ex.: a empresa em que a equipe Fluxus está trabalhando). */
  sidebarHeader?: ReactNode;
  user: AuthUser;
  onLogout: () => Promise<void>;
  /** Ações do topo antes do tema e da conta (ex.: o sino de notificações). */
  headerActions?: ReactNode;
  children: ReactNode;
};

/**
 * Estrutura das telas autenticadas.
 *  - celular (< 768px): barra superior + gaveta com o menu completo + barra inferior com atalhos;
 *  - tablet (768–1023px): trilho lateral só com ícones (dica com o nome);
 *  - desktop (≥ 1024px): menu lateral completo, agrupado como no PRD §21.
 */
export function AppShell({ items, sidebarHeader, user, onLogout, headerActions, children }: AppShellProps) {
  const sections = buildNavSections(items);
  const bottomItems = mobilePrimaryItems(items);

  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-(--z-index-tooltip) focus:rounded-md focus:bg-surface-raised focus:px-3 focus:py-2 focus:shadow-md"
      >
        Pular para o conteúdo
      </a>

      {/* Trilho (tablet) e menu lateral (desktop) */}
      <aside
        aria-label="Menu principal"
        className="fixed inset-y-0 left-0 z-(--z-index-sticky) hidden flex-col border-r border-border bg-surface md:flex md:w-16 lg:w-64"
      >
        <div className="flex h-16 items-center px-4 lg:px-5">
          <BrandLogo compact className="lg:hidden" />
          <BrandLogo className="hidden lg:inline-flex" />
        </div>
        <nav className="flex-1 overflow-y-auto px-2 pb-4 lg:px-3">
          {sidebarHeader}
          <SidebarSections sections={sections} />
        </nav>
      </aside>

      <div className="flex min-h-dvh flex-col md:pl-16 lg:pl-64">
        <header className="sticky top-0 z-(--z-index-sticky) flex h-14 items-center gap-2 border-b border-border bg-background/90 px-3 backdrop-blur-sm supports-[backdrop-filter]:bg-background/75 md:h-16 md:px-6">
          <MobileMenu sections={sections} header={sidebarHeader} />
          <BrandLogo className="md:hidden" />
          <div className="ml-auto flex items-center gap-1">
            {headerActions}
            <ThemeMenu />
            <UserMenu user={user} onLogout={onLogout} />
          </div>
        </header>

        <main
          id="conteudo"
          tabIndex={-1}
          className={cn('flex-1 outline-none', bottomItems.length && 'pb-20 md:pb-0')}
        >
          {children}
        </main>
      </div>

      {bottomItems.length ? <BottomNav items={bottomItems} /> : null}
    </div>
  );
}

const linkBase =
  'group flex items-center gap-3 rounded-md text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground aria-[current=page]:bg-brand-soft aria-[current=page]:font-semibold aria-[current=page]:text-brand-strong dark:aria-[current=page]:text-brand';

function SidebarSections({ sections }: { sections: NavSection[] }) {
  return (
    <div className="flex flex-col gap-5">
      {sections.map((section) => (
        <div key={section.id}>
          <p className="mb-1.5 hidden px-3 text-xs font-semibold text-muted-foreground lg:block">
            {section.label}
          </p>
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => (
              <li key={item.to}>
                <Tooltip
                  content={item.badge ? `${item.label} (${item.badge.label})` : item.label}
                  side="right"
                >
                  <NavLink
                    to={item.to}
                    className={cn(linkBase, 'h-10 justify-center px-0 lg:justify-start lg:px-3')}
                  >
                    <span className="relative">
                      <item.icon className="size-5 shrink-0" aria-hidden />
                      {item.badge?.count ? (
                        <span
                          aria-hidden
                          className="absolute -top-1.5 -right-1.5 size-2.5 rounded-full bg-status-red-solid ring-2 ring-background lg:hidden"
                        />
                      ) : null}
                    </span>
                    <span className="sr-only lg:not-sr-only">{item.label}</span>
                    <NavBadge badge={item.badge} className="ml-auto hidden lg:inline-flex" />
                    {item.badge?.count ? <span className="sr-only">{item.badge.label}</span> : null}
                  </NavLink>
                </Tooltip>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function MobileMenu({ sections, header }: { sections: NavSection[]; header?: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Abrir menu">
          <Menu aria-hidden />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" title="Menu" hideHeader>
        <div className="flex h-14 items-center px-4">
          <BrandLogo />
        </div>
        <nav aria-label="Menu principal" className="flex-1 overflow-y-auto px-3 pb-6">
          {header}
          <div className="flex flex-col gap-5">
            {sections.map((section) => (
              <div key={section.id}>
                <p className="mb-1.5 px-3 text-xs font-semibold text-muted-foreground">{section.label}</p>
                <ul className="flex flex-col gap-0.5">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={cn(linkBase, 'h-11 px-3')}
                      >
                        <item.icon className="size-5 shrink-0" aria-hidden />
                        {item.label}
                        <NavBadge badge={item.badge} className="ml-auto inline-flex" />
                        {item.badge?.count ? <span className="sr-only">{item.badge.label}</span> : null}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

/** Contador visual do item do menu (ex.: alertas abertos); o texto para leitores de tela fica fora dele. */
function NavBadge({ badge, className }: { badge?: NavItem['badge']; className?: string }) {
  if (!badge?.count) return null;
  return (
    <span
      aria-hidden
      className={cn(
        'min-w-5 items-center justify-center rounded-full bg-status-red-bg px-1.5 text-[0.6875rem] font-bold text-status-red-fg tabular-nums',
        className,
      )}
    >
      {badge.count > 99 ? '99+' : badge.count}
    </span>
  );
}

function BottomNav({ items }: { items: NavItem[] }) {
  const { pathname } = useLocation();
  return (
    <nav
      aria-label="Atalhos"
      className="fixed inset-x-0 bottom-0 z-(--z-index-sticky) border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm md:hidden"
    >
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((item) => {
          const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={cn(
                  'flex h-16 flex-col items-center justify-center gap-1 text-[0.6875rem] font-semibold',
                  active ? 'text-brand-strong dark:text-brand' : 'text-muted-foreground',
                )}
              >
                <item.icon className="size-5" aria-hidden />
                {item.label}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
